import { Calc } from "../math/Calc.js";
import { event } from "../util/EventUtil.js";
import { Resize, ResizeSignal } from "./Resize.js";

export interface IListData {
    id: any;
    height?: number;

    /**
     * @deprecated Use List.setFilter() instead.
     */
    visible?: boolean;
}

export interface IListVisual {
    element: HTMLDivElement;
}

export interface IListProvider<Data extends IListData, Visual extends IListVisual = IListVisual> {
    prepare(visual: Visual): void;
    update(data: Data, visual: Visual): void;
    dataList: Data[];
}

interface DataLookup<Data extends IListData, Visual extends IListVisual> {
    index: number;
    data?: Data;
    visual?: Visual;
    element: HTMLDivElement;
}

export interface ScrollOptions {
    tween?: number;
    align?: "top" | "bottom" | "auto";
    marginTop?: number;
    marginBottom?: number;
    ifNeeded?: boolean;
}

export class List<Data extends IListData = IListData, Visual extends IListVisual = IListVisual> {
    private _element: HTMLElement = null;
    private _provider: IListProvider<Data, Visual> = null;
    private _content: HTMLDivElement;
    private _buffer: DataLookup<Data, Visual>[] = [];
    private _visible: boolean = true;
    private _onResize: ResizeSignal;
    private _scrollListener: DisposableObject = null;
    private _dataVisible = new Map<any, boolean>();
    private _filter: (data: Data) => boolean = (data) => data.visible;
    private _reducedMotion: MediaQueryList;

    constructor(
        private _height: number,
        element: HTMLElement = null,
        private _offset: number = 0) {
        this._content = document.createElement("div");
        this._content.classList.add("e5-list-content");
        this._onResize = Resize.create();
        this._onResize.add(
            () => {
                this.update();
            }
        );

        this._reducedMotion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

        this.element = element;
    }

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(visible: boolean) {
        if (this._visible === visible) {
            return;
        }
        this._visible = visible;
        this._content.classList.toggle("e5-hidden", !visible);
        if (visible) {
            this.update();
        }
    }

    public scrollTo(item: Data, options: ScrollOptions = {}): void {
        if (!this._visible
            || !item
            || !this._element
            || !this._provider
            || !this._dataVisible.get(item.id)
        ) {
            return;
        }

        const items = this.getDataList();
        const index = items.indexOf(item);
        if (index === -1) {
            console.warn("You cannot scroll to an element which is not part of the provider items.");
            return;
        }

        let align = options.align || "auto";
        const tween = options.tween === undefined ? 0 : options.tween;
        const ifNeeded = options.ifNeeded === undefined ? true : options.ifNeeded;
        const marginTop = options.marginTop === undefined ? 0 : options.marginTop;
        const marginBottom = options.marginBottom === undefined ? 0 : options.marginBottom;

        const offsetTop = (index * this._height) - marginTop + this._content.offsetTop - this._element.scrollTop;
        const offsetBottom = this._element.clientHeight - (marginBottom + (index + 1) * this._height + this._content.offsetTop - this._element.scrollTop);

        if (ifNeeded && offsetTop >= 0 && offsetBottom >= 0) {
            return;
        }

        if (align === "auto") {
            if (offsetTop < 0) {
                align = "top";
            }
            else {
                align = "bottom";
            }
        }

        let scrollTop: number;
        if (align === "top") {
            scrollTop = index * this._height + this._content.offsetTop - marginTop;
        }
        else if (align === "bottom") {
            scrollTop = index * this._height + this._content.offsetTop;
            scrollTop -= this._element.clientHeight - this._height - marginBottom;
        }

        this.scrollTop(scrollTop, tween);
    }

    public scrollTop(scrollTop: number, tween: number = 0): void {
        if (!this._visible || !this._element)
            return;
        scrollTop = Calc.clamp(scrollTop, 0, this._element.scrollHeight - this._element.clientHeight);

        if (tween > 0
            && this._reducedMotion
            && this._reducedMotion.matches) {
            tween = 0;
        }


        if (tween <= 0) {
            this._element.scrollTop = scrollTop;
            // this.update();
        } else {
            gsap.to(this._element, { duration: tween, scrollTop: scrollTop });
            // gsap.to(this._element, { duration: tween, scrollTop: scrollTop, onUpdate: () => this.update() });
        }
    }

    public refresh(): void {
        for (var lookupItem of this._buffer) {
            lookupItem.index = -1;
            if (lookupItem.element) {
                lookupItem.element.remove();
            }
        }

        this._dataVisible.clear();
        if (this._provider && typeof this._filter !== "function") {
            for (let data of this._provider.dataList) {
                this._dataVisible.set(data.id, true);
            }
        }
        else if (this._provider && typeof this._filter === "function") {
            for (let data of this._provider.dataList) {
                this._dataVisible.set(data.id, this._filter(data));
            }
        }

        this.update();
    }

    public setFilter(value: (data: Data) => boolean) {
        this._filter = value;
        this.refresh();
    }

    public updateData(data: Data): void {
        if (!data) {
            return;
        }

        if (typeof this._filter === "function") {
            this._dataVisible.set(data.id, this._filter(data));
        } else {
            this._dataVisible.set(data.id, true);
        }

        for (var dataLookup of this._buffer) {
            if (dataLookup.data === data) {
                this._provider.update(dataLookup.data, dataLookup.visual);
            }
        }
    }

    private getDataList(): Data[] {
        var items: Data[] = [];
        for (var item of this._provider.dataList) {
            if (this._dataVisible.get(item.id)) {
                items.push(item);
            }
        }
        return items;
    }

    public get height(): number {
        return this._height;
    }

    public set height(value: number) {
        if (this._height == value) {
            return;
        }
        this._height = value;
        this.refresh();
    }

    private update(): void {
        if (!this._visible || !this._element || !this._provider) {
            this._content.style.height = "0";
            return;
        }

        //Get all visible items and
        const dataList = this.getDataList();
        const height = dataList.length * this._height;
        this._content.style.height = (height + this._offset) + "px";

        const countOffset = 2;

        //Fillup item buffer.
        const countLookupItems = Math.ceil(this._element.offsetHeight / this._height) + 2 + countOffset;
        if (this._buffer.length < countLookupItems) {
            const diff = countLookupItems - this._buffer.length;
            for (let i = 0; i < diff; ++i) {
                const element = document.createElement("div");
                element.classList.add("e5-list-item");
                // element.style.height = this._provider.height + "px";
                const visual: Visual = <any>{ element: element };
                const lookup: DataLookup<Data, Visual> = { visual: visual, index: -1, element: element, data: null };
                this._provider.prepare(visual);
                this._buffer.push(lookup);
            }
        }

        let firstIndex = Math.floor(Calc.clampMin(this._element.scrollTop - this._content.offsetTop, 0) / this._height);
        let maxIndex = Calc.clamp(firstIndex + (countLookupItems - (1 + countOffset)), 0, dataList.length - 1);

        firstIndex = Calc.clampMin(firstIndex - 1, 0);
        maxIndex = Calc.clampMax(maxIndex + 1, dataList.length - 1);

        //Remove and reset all lookup items which are not needed.
        for (let dataLookup of this._buffer) {
            if (dataLookup.index !== -1 && (dataLookup.index < firstIndex || dataLookup.index > maxIndex)) {
                dataLookup.element.remove();
                dataLookup.index = -1;
                dataLookup.data = null;
            }
        }

        //Update and move the item to correct position.
        let countActive = 0;
        for (let dataIndex = firstIndex; dataIndex <= maxIndex; ++dataIndex) {
            if (!dataList[dataIndex]) {
                console.warn(`Data list lookup for data-index ${dataIndex} failed.`);
                continue;
            }

            const dataLookup = this.getDataLookup(dataIndex);
            if (!dataLookup) {
                console.warn(`Data lookup for data-index ${dataIndex} failed.`);
                continue;
            }

            if (dataLookup.index === -1) {
                dataLookup.data = dataList[dataIndex];
                dataLookup.element.style.top = (dataIndex * this._height) + "px";
                dataLookup.index = dataIndex;
                this._provider.update(dataLookup.data, dataLookup.visual);
                // dataLookup.visual.element.style.backgroundColor = "#" + Text.fillHead(Math.floor((0xFFFFFF * Math.random())).toString(16), 6, "0");

                //Keep tab order and focus
                if (countActive >= this._content.children.length) {
                    this._content.appendChild(dataLookup.element)
                } else {
                    this._content.insertBefore(dataLookup.element, this._content.children[countActive]);
                }
            }

            countActive++;
        }
    }

    private getDataLookup(index: number): DataLookup<Data, Visual> {
        for (let item of this._buffer) {
            if (item.index === index) {
                return item;
            }
        }
        for (let item of this._buffer) {
            if (item.index === -1) {
                return item;
            }
        }
        return null;
    }

    public get element(): HTMLElement {
        return this._element;
    }

    public set element(element: HTMLElement) {
        if (this._element === element) {
            return;
        }

        if (this._scrollListener) {
            this._scrollListener.dispose();
            this._scrollListener = null;
        }

        if (this._element) {
            this._element.classList.remove("e5-list");
            this._content.remove();
        }

        this._element = element;

        if (this._element) {
            this._element.classList.add("e5-list");
            this._element.appendChild(this._content);
            this._scrollListener = event.on(
                {
                    type: "scroll",
                    passive: true,
                    scope: this._element,
                    listener: (e) => {
                        this.update();
                    }
                }
            );
            this.refresh();
        }
        this._onResize.element = element;
    }

    public get provider(): IListProvider<Data, Visual> {
        return this._provider;
    }

    public set provider(provider: IListProvider<Data, Visual>) {
        if (this._provider === provider) {
            return;
        }
        this._provider = provider;
        this.refresh();
    }

    public get contentElement(): HTMLDivElement {
        return this._content;
    }
}